/**
 * useMonthlyData.ts - 월별 데이터 로드/저장 및 미달성 조정 로직
 *
 * Phase 2: payroll-management.tsx 모듈 분리
 * FR-14: Monthly data loading, import, and adjustment logic
 */

import { useEffect, useCallback, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import type { Employee, BudgetData } from '../../contexts/AppContext.tsx';
import { getSafeMonthlyData } from '../../utils/payrollDataUtils.ts';
import {
  logDataOperation,
  logAdjustment,
  logUserAction,
  logBusinessEvent,
  logError,
  logDebugDetails,
  startLogGroup,
  endLogGroup,
} from '../../utils/payrollLogger.ts';
import { getBudgetData, saveBudgetMonthReplace } from '../../services/firebase/budgetFirebaseService.ts';
import { resolveRetirementPay, calculateTeamSalaryTotal, calculatePureTargetSales, clearPartPayFields } from '../../utils/payrollCalculations.ts';

const IS_DEVELOPMENT = process.env.NODE_ENV === 'development';

export interface UseMonthlyDataParams {
  // Core state
  selectedMonth: string;
  budgetData: BudgetData;
  employees: Employee[];
  globalEmployees: Employee[];
  localTeams: string[];

  // Monthly data states
  previousMonthData: any;
  showComparisonWithPreviousMonth: boolean;
  importedFromMonth: string | null;

  // Adjustment states
  applyPreviousMonthAdjustment: boolean;
  adjustmentAppliedTeams: Set<string>;
  originalTargetSales: Record<string, number>;
  isLoadingPreviousMonthData: boolean;
  isBulkAdjustmentRunning: boolean;
  manualToggleTimestamp: number;
  underperformanceDeductionRate: number;

  // Refs
  isSavingRef: React.MutableRefObject<boolean>;
  isAddingEmployeeRef: React.MutableRefObject<boolean>;

  // Setters
  setBudgetData: React.Dispatch<React.SetStateAction<BudgetData>>;
  setEmployees: React.Dispatch<React.SetStateAction<Employee[]>>;
  setPreviousMonthData: React.Dispatch<React.SetStateAction<any>>;
  setPreviousMonthEmployees: React.Dispatch<React.SetStateAction<Employee[]>>;
  setShowComparisonWithPreviousMonth: React.Dispatch<React.SetStateAction<boolean>>;
  setApplyPreviousMonthAdjustment: React.Dispatch<React.SetStateAction<boolean>>;
  setAdjustmentAppliedTeams: React.Dispatch<React.SetStateAction<Set<string>>>;
  setOriginalTargetSales: React.Dispatch<React.SetStateAction<Record<string, number>>>;
  setOriginalEmployees: React.Dispatch<React.SetStateAction<Employee[]>>;
  setOriginalTeams: React.Dispatch<React.SetStateAction<any[]>>;
  setOriginalBudgetData: React.Dispatch<React.SetStateAction<any>>;
  setIsLoadingPreviousMonthData: React.Dispatch<React.SetStateAction<boolean>>;
  setIsBulkAdjustmentRunning: React.Dispatch<React.SetStateAction<boolean>>;
  setManualToggleTimestamp: React.Dispatch<React.SetStateAction<number>>;
  setUnderperformanceDeductionRate: React.Dispatch<React.SetStateAction<number>>;
  setImportedFromMonth: React.Dispatch<React.SetStateAction<string | null>>;
  setIsDataChanged: React.Dispatch<React.SetStateAction<boolean>>;
  setChangeLog: React.Dispatch<React.SetStateAction<string[]>>;

  // Functions from component/other hooks
  addChangeLog: (msg: string) => void;

  // From usePayrollCalculations
  calculateFinalTargetSales: (teamName: string, pureTarget: number, teamEmployees: Employee[], context: string) => any;
}

export function useMonthlyData(params: UseMonthlyDataParams) {
  const {
    selectedMonth, budgetData, employees, globalEmployees, localTeams,
    previousMonthData, showComparisonWithPreviousMonth, importedFromMonth,
    applyPreviousMonthAdjustment, adjustmentAppliedTeams, originalTargetSales,
    isLoadingPreviousMonthData, isBulkAdjustmentRunning, manualToggleTimestamp,
    underperformanceDeductionRate,
    isSavingRef, isAddingEmployeeRef,
    setBudgetData, setEmployees, setPreviousMonthData, setPreviousMonthEmployees,
    setShowComparisonWithPreviousMonth, setApplyPreviousMonthAdjustment,
    setAdjustmentAppliedTeams, setOriginalTargetSales, setOriginalEmployees,
    setOriginalTeams, setOriginalBudgetData, setIsLoadingPreviousMonthData,
    setIsBulkAdjustmentRunning, setManualToggleTimestamp,
    setUnderperformanceDeductionRate, setImportedFromMonth,
    setIsDataChanged, setChangeLog,
    addChangeLog,
    calculateFinalTargetSales,
  } = params;

  // ═══════════════════════════════════════
  // Internal refs (moved from component)
  // ═══════════════════════════════════════

  // 월별 데이터 로드 ref (중복 실행 방지)
  const lastLoadedMonthRef = useRef<string>('');
  const isLoadingDataRef = useRef<boolean>(false);

  // 🔧 가드 스킵 재시도 tick (가드에 걸리면 해제 시점에 deps가 안 변해 영구 스킵되던 버그 방지 — 실제매출 F5 로더와 동일 관용구)
  const [monthLoadRetryTick, setMonthLoadRetryTick] = useState(0);
  // 🔒 월 데이터 로드 완료 신호 (ref와 달리 소비측 이펙트를 재트리거하는 state — 월 전환 직후 이전 달 로스터로 재계산되는 것 방지용)
  const [loadedMonth, setLoadedMonth] = useState<string>('');

  // 현재 선택된 월의 데이터 로드
  useEffect(() => {
    let cancelled = false;
    // 🔧 저장 중, 직원 추가 중, 데이터 로딩 중일 때는 실행하지 않음 — 가드 해제 후 재시도 예약 (영구 스킵 방지)
    if (isSavingRef.current || isAddingEmployeeRef.current || isLoadingDataRef.current) {
      setTimeout(() => { if (!cancelled) setMonthLoadRetryTick(t => t + 1); }, 300);
      return () => { cancelled = true; };
    }

    // budgetData 유효성 검사 강화
    if (!budgetData || typeof budgetData !== 'object') {
      logDebugDetails('월별 데이터 로드 스킵 - budgetData 무효', { selectedMonth });
      return;
    }

    // 🔧 이미 해당 월 데이터를 로드했으면 스킵
    if (lastLoadedMonthRef.current === selectedMonth && employees.length > 0) {
      logDebugDetails('월별 데이터 로드 스킵 - 이미 로드됨', {
        lastLoadedMonth: lastLoadedMonthRef.current,
        selectedMonth,
        employeesCount: employees.length
      });
      return;
    }
    
    // 🚨 월 변경 전 변경사항 저장 확인 - 변수가 정의된 후에만 실행
    // isDataChanged와 internalSaveAllData는 나중에 정의되므로 여기서는 제거
    // 실제 저장 확인 로직은 별도 useEffect로 이동 필요
    
    // 🔧 로딩 중 플래그 설정
    isLoadingDataRef.current = true;
    
    startLogGroup('월별 데이터 로드');
    logDataOperation('월별 데이터 로드 시작', {
      selectedMonth,
      hasMonthlyData: !!budgetData.monthlyData,
      employeesCountInSelectedMonth: budgetData.monthlyData?.[selectedMonth]?.employees?.length || 0,
      currentEmployeesCount: employees.length
    });

    // 🔧 월 변경 시 원본 데이터 초기화
    logDataOperation('월 변경으로 인한 원본 데이터 초기화', { selectedMonth });
    setOriginalEmployees([]);
    setOriginalTeams([]);
    setOriginalBudgetData(null);

    // 🎯 Firebase 우선 데이터 로드 로직
    let employeesToLoad: Employee[] = [];
    
    // 🎯 월별 데이터 완전 독립성 보장
    
    // 월별 저장된 데이터만 사용 (완전한 깊은 복사)
    if (budgetData.monthlyData && budgetData.monthlyData[selectedMonth]?.employees) {
      // 🔧 완전한 불변성을 위한 JSON 깊은 복사
      employeesToLoad = JSON.parse(JSON.stringify(budgetData.monthlyData[selectedMonth].employees!));
      logDataOperation('월별 저장 데이터 사용', {
        selectedMonth,
        loadedEmployeesCount: employeesToLoad.length,
        importedFromMonth: budgetData.monthlyData[selectedMonth].importedFromMonth
      });
      
      // 🔧 실제로 다른 월에서 가져온 데이터인지 확인
      const storedImportedFrom = budgetData.monthlyData[selectedMonth].importedFromMonth;
      if (storedImportedFrom && storedImportedFrom !== selectedMonth) {
        setImportedFromMonth(storedImportedFrom);
      } else {
        setImportedFromMonth(null);
      }
    } 
    // 🚨 전역 데이터 fallback 제거 (월별 독립성 보장)
    // else if (globalEmployees && globalEmployees.length > 0) {
    //   employeesToLoad = [...globalEmployees];
    //   setImportedFromMonth(null);
    //   setIsDataChanged(true);
    //   addChangeLog("전역 데이터에서 월별 데이터로 전환");
    // } 
    
    // 월별 데이터가 없으면 빈 배열 (전역 데이터 사용 안함)
    else {
      employeesToLoad = [];
      logDataOperation('월별 데이터 없음 - 빈 상태로 시작', { selectedMonth });
      setImportedFromMonth(null);
    }
    
    // 🔧 상태 업데이트를 배치로 처리하여 재렌더링 최소화
    setTimeout(() => {
      setEmployees(employeesToLoad);
      setShowComparisonWithPreviousMonth(false);
      setPreviousMonthEmployees([]);
      setChangeLog([]);
      setIsDataChanged(false);
      
      // 🔧 로드 완료 후 플래그 업데이트
      lastLoadedMonthRef.current = selectedMonth;
      isLoadingDataRef.current = false;
      setLoadedMonth(selectedMonth); // 🔒 로드 완료 신호 (인센티브 재계산 이펙트의 월 전환 가드 해제)
      
      logDataOperation('월별 데이터 로드 완료', {
        selectedMonth,
        finalEmployeesCount: employeesToLoad.length,
        dataSource: budgetData.monthlyData?.[selectedMonth]?.employees ? '월별저장데이터' : 
                    (globalEmployees.length > 0 ? 'Firebase전역데이터' : '빈데이터')
      });
      endLogGroup();
    }, 0);
  }, [selectedMonth, !!budgetData?.monthlyData?.[selectedMonth], globalEmployees?.length, monthLoadRetryTick]); // 🚀 의존성 최적화: 필요한 값만 (+가드 스킵 재시도 tick)
  
  // 지난달 데이터 로드 (비교 모드가 활성화될 때)
  useEffect(() => {
    if (showComparisonWithPreviousMonth) {
      // 날짜를 파싱해서 한 달 전 날짜 계산
      const [year, month] = selectedMonth.split('-').map(Number);
      let previousMonth = month - 1;
      let previousYear = year;
      
      if (previousMonth === 0) {
        previousMonth = 12;
        previousYear -= 1;
      }
      
      const previousMonthKey = `${previousYear}-${previousMonth.toString().padStart(2, '0')}`;
      
      // 지난달 데이터가 있는지 확인 (완전한 깊은 복사)
      if (budgetData.monthlyData && budgetData.monthlyData[previousMonthKey]?.employees) {
        setPreviousMonthEmployees(JSON.parse(JSON.stringify(budgetData.monthlyData[previousMonthKey].employees!)));
      } else {
        setPreviousMonthEmployees([]);
        if (showComparisonWithPreviousMonth) {
          alert(`${previousMonthKey} 월 데이터가 없어 비교할 수 없습니다.`);
          setShowComparisonWithPreviousMonth(false);
        }
      }
    }
  }, [showComparisonWithPreviousMonth, selectedMonth, budgetData.monthlyData]);

  // 🔧 미달성팀 차감 비율 초기화 useEffect
  useEffect(() => {
    // budgetData에서 차감 비율 가져오기 (월별 데이터 우선, 없으면 전역 데이터)
    const monthlyDeductionRate = budgetData.monthlyData?.[selectedMonth]?.underperformanceDeductionRate;
    const globalDeductionRate = budgetData.underperformanceDeductionRate;
    
    const initialRate = monthlyDeductionRate ?? globalDeductionRate ?? 50;
    
    logDataOperation(`차감비율 초기화 - ${selectedMonth}월`, {
      monthlyDeductionRate,
      globalDeductionRate,
      initialRate
    });
    
    setUnderperformanceDeductionRate(initialRate);
  }, [selectedMonth, budgetData.monthlyData, budgetData.underperformanceDeductionRate]);

  // 지난달 미달성 금액 조정 기능 활성화 시 지난달 데이터 자동 로드
  useEffect(() => {
    logAdjustment(`useEffect 실행`, {
      selectedMonth,
      applyPreviousMonthAdjustment,
      timestamp: new Date().toISOString()
    });

    if (applyPreviousMonthAdjustment) {
      loadPreviousMonthData();
    } else {
      setPreviousMonthData(null);
      // 🔧 조정 해제 시 원본 목표매출 복원
      restoreOriginalTargetSales();
    }
  }, [applyPreviousMonthAdjustment, selectedMonth]);

  // ═══════════════════════════════════════
  // Monthly Data Import
  // ═══════════════════════════════════════

  // 월별 데이터 불러오기 함수 (기본값: 지난달) - useCallback 최적화
  // ── 불러오기 코어 (수동 버튼·월 첫 열람 자동 실행 공용) ──────────────
  // 규칙 단일 정의:
  //  - 직원: 복사하되 실적 파생값(incentive·연장근로시간·초과수당·개별수당)은 0 리셋,
  //    퇴직금은 리셋값 기준 정본 재계산(세전÷12)
  //  - 상수 복사: 팀·인건비율·차감율·인센티브 비율·150% 토글
  //  - teamTargetSales·teamPureTargetSales·teamSalaryTotals: 리셋 로스터 기준 정본 재계산
  //    (소스 복사 아님 — 조정 이월분도 자연히 미포함)
  //  - 실적/매핑 파이프라인 필드(doctorTeamMapping·teamActualSales·teamPerformanceData) 비접촉,
  //    teamIncentiveCalculations 리셋, importedFromMonth 기록
  // 자동/수동 어느 쪽이든 반드시 이 코어만 사용할 것 — 복사 규칙 이원화 금지.
  const executeImportFromMonth = useCallback(async (
    sourceMonth: string,
    options: { silent?: boolean; sourceData?: any } = {}
  ): Promise<{ success: boolean; dataInfoText: string }> => {
    const currentMonthKey = selectedMonth;
    // 🔧 복사 원본 결정: 자동 경로는 Firestore 신선 스냅샷(options.sourceData = 소스 월 레코드)을
    //    원본으로 사용 — budget onSnapshot이 없어 장수 탭 state가 동결되므로, state 기준 복사는
    //    다른 세션이 최종 수정한 로스터 대신 구버전(옛 급여·퇴사자 포함)을 새 달로 silent 저장할 수 있음.
    //    수동 경로는 기존대로 화면 state 기준(시그니처 하위호환).
    const selectedMonthData = options.sourceData ?? budgetData.monthlyData?.[sourceMonth];
    if (!selectedMonthData) {
      if (!options.silent) {
        alert(`${sourceMonth} 월 데이터가 없습니다.\n\n해당 월에 데이터를 먼저 입력한 후 다시 시도해주세요.`);
      }
      return { success: false, dataInfoText: '' };
    }
    try {

      // 🔧 복사 시 퇴직금 정본 산식 정규화 — 원본 월의 어긋난 레거시 retirement가 대상 월로 복제·영속되는 것 방지
      // 🧹 실적 파생값 리셋 (2026-07-05 결정): incentive와 함께 연장근로시간·초과수당·개별수당도
      //    '그 달 실제 발생값'이므로 새 달은 0에서 시작한다 (teamIncentiveCalculations 리셋과 대칭).
      //    퇴직금 = (세전+초과+개별)÷12 — 리셋된 값 기준이므로 세전÷12로 시작.
      const importedEmployees = (selectedMonthData.employees || []).map((emp: Employee) => {
        const reset = {
          ...emp,
          incentive: 0,
          overtimeMinutes: 0,
          overtime: 0,
          individual: 0,
          retirement: resolveRetirementPay({ ...emp, overtime: 0, individual: 0 })
        };
        // 🔒 파트는 개별수당으로만 급여를 지급한다(정책) — 화면에서 잠긴 고정급 필드는 정리해서 넘긴다.
        //    잠금은 모든 파트 행에 걸리지만 0 정리는 직위 전환 시점에만 돌므로, 이 경로가 없으면
        //    이전 달의 잔존 고정급(예: 신규 추가 기본 월급 270만)이 잠긴 채 매달 이월되며 목표를 부풀린다.
        return reset.position === '파트' ? clearPartPayFields(reset) : reset;
      });

      // 🎯 목표매출·순수목표·인건비합계: 리셋된 로스터(수당 0) 기준으로 정본 재계산 —
      //    수당 리셋으로 인건비합계가 소스 달과 달라지므로 소스 pure 복사는 어긋난 쌍이 됨.
      //    (로스터·totals·목표 일관 쌍 원칙 — saveSpecificMonthData 정규화 재계산과 대칭.
      //     조정 미복사 원칙도 자동 충족: 재계산 순수값이므로 이월분이 애초에 없음)
      const importedTeamsList: string[] = selectedMonthData.teams ? [...selectedMonthData.teams] : [];
      const recomputeTeams = [...new Set([
        ...importedTeamsList,
        ...importedEmployees.map((e: Employee) => e.team)
      ])].filter((t): t is string => !!t && t !== '미지정');
      const copiedTeamPcts: Record<string, number> = selectedMonthData.teamPersonnelPercentages || {};
      const importFallbackPct = selectedMonthData.personnelPercentage ?? budgetData.personnelPercentage ?? 0;
      const recomputedSalaryTotals: Record<string, number> = {};
      const recomputedPureTargets: Record<string, number> = {};
      recomputeTeams.forEach(team => {
        const salaryTotal = calculateTeamSalaryTotal(importedEmployees, team, importedEmployees);
        recomputedSalaryTotals[team] = salaryTotal;
        recomputedPureTargets[team] = calculatePureTargetSales(salaryTotal, copiedTeamPcts[team] ?? importFallbackPct);
      });

      // 현재 월에 적용할 '급여 소유 필드' 묶음 (deterministic — prev 무관, 아래 함수형 업데이터와 저장 페이로드가 공유).
      //  - #5: applyOvertimeMultiplier는 대상 월 기존 토글을 우선(새 달에 선설정한 150% 토글이 소스 값으로 강등되지 않게).
      //  - #2: adjustmentApplied·applyPreviousMonthAdjustment도 리셋 — 목표 3종이 순수 재계산값(이월분 없음)이라
      //    '플래그 true=저장 목표에 이월 포함' 불변식 정합을 위해 조정 이력을 플래그·토글까지 함께 초기화.
      //  ⚠️ 파이프라인 소유 필드(teamActualSales·teamPerformanceData)는 여기 미포함 — 함수형 업데이터에서
      //    prev 값을 그대로 보존한다(급여는 이들을 '재계산 시에만 기록'하므로 불러오기는 비접촉).
      const ownedMonthFields = {
        employees: importedEmployees,
        teams: selectedMonthData.teams ? [...selectedMonthData.teams] : [], // 🔧 UI 상태 동기화를 위해 teams 필드 추가
        teamTargetSales: {...recomputedPureTargets}, // 팀별 목표매출: 리셋 로스터 기준 재계산 순수값 (조정 이월분 없음)
        // 전체 목표 = 팀별 합 — 스칼라를 갱신하지 않으면 소스 월 값이 남아 팀맵과 어긋난다 (Codex 재심 P1-4)
        targetSales: Object.values(recomputedPureTargets).reduce((sum: number, v: any) => sum + (Number(v) || 0), 0),
        teamPureTargetSales: {...recomputedPureTargets}, // 팀별 순수목표매출: 동일 재계산값
        teamSalaryTotals: {...recomputedSalaryTotals}, // 팀별 인건비합계: 로스터·목표와 일관 쌍
        // 🎯 '팀별 목표매출 & 인센티브' 상수 복사 (사용자가 설정해 유지되는 값 - 월별 저장이라 미복사 시 유실됨)
        personnelPercentage: selectedMonthData.personnelPercentage ?? budgetData.personnelPercentage, // 전체 인건비율
        teamPersonnelPercentages: selectedMonthData.teamPersonnelPercentages ? {...selectedMonthData.teamPersonnelPercentages} : {}, // 팀별 인건비율 (전역 fallback 없음 → 반드시 복사)
        underperformanceDeductionRate: selectedMonthData.underperformanceDeductionRate ?? budgetData.underperformanceDeductionRate ?? 50, // 차감율
        teamIncentiveRatios: selectedMonthData.teamIncentiveRatios ? {...selectedMonthData.teamIncentiveRatios} : {}, // 팀별 인센티브 비율/배분 (월별화 → 반드시 복사, incentiveRatios 전역 기본값은 동결 유지)
        // ⏰ 연장근로 150% 토글 — 사업장 규모(5인 기준) 판정은 달이 바뀌어도 유지되는 설정.
        //    대상 월에 이미 선설정된 토글이 있으면 그 값을 우선(새 달 선설정 150%가 소스 값으로 강등되어
        //    첫 연장근로 입력이 1.0배로 계산되는 문제 방지), 없으면 소스 월 값, 둘 다 없으면 false.
        applyOvertimeMultiplier: budgetData.monthlyData?.[currentMonthKey]?.applyOvertimeMultiplier ?? selectedMonthData.applyOvertimeMultiplier ?? false,
        // 🧹 급여 소유 파생 스냅샷은 초기화 (이번 달 일일보고서/입력으로 재계산됨)
        teamIncentiveCalculations: {}, // 팀별 인센티브 산출 결과: 재계산
        // 🔒 #2 조정 이력 리셋: 목표가 순수 재계산값(이월분 없음)이므로 플래그·토글도 '조정 없음'으로 통일
        adjustmentApplied: {}, // 팀별 조정 적용 플래그 전부 소거 (플래그 true=이월 포함 불변식 정합)
        teamAdjustmentCarryover: {}, // 이월금액도 소거 — 목표가 순수 재계산값이므로 이월 0
        applyPreviousMonthAdjustment: false, // 지난달 미달성 조정 토글도 off로 리셋(주석·불러오기 리셋 계약 일치)
        importedFromMonth: sourceMonth, // 어디서 가져왔는지 정보 추가
        lastUpdated: new Date().toISOString() // 마지막 업데이트 시간 추가
      };

      // 상태 업데이트 — #8: 함수형 업데이터로 prev 기준 대상 월의 급여 소유 필드만 덧씌운다.
      //   클로저 스냅샷 통째 교체(setBudgetData(updatedBudgetData))는 저장/재조회 대기 창의 다른
      //   파이프라인 쓰기(teamActualSales·teamPerformanceData 캐시 등)를 롤백시키므로 금지.
      //   ...ownedMonthFields에 미포함인 파이프라인 소유 필드는 prev 값 그대로 유지된다.
      setBudgetData(prev => ({
        ...prev,
        monthlyData: {
          ...prev.monthlyData,
          [currentMonthKey]: {
            ...(prev.monthlyData?.[currentMonthKey] || {}),
            ...ownedMonthFields
          }
        }
      }));

      // 현재 컴포넌트의 직원 목록도 업데이트 (정규화본과 동일)
      setEmployees([...importedEmployees]);
      setImportedFromMonth(sourceMonth);
      // 🔒 #2 세션 조정 상태도 리셋 — 불러오기=조정 이력 리셋 계약을 세션 플래그·토글까지 일관 적용.
      //    목표가 순수 재계산값(이월분 없음)이라 조정 '적용됨' 세션 표시가 남으면 UI·불변식 모순.
      //    (applyPreviousMonthAdjustment가 true였다면 off 전환이 이펙트로 restoreOriginalTargetSales를
      //     타지만, 이미 adjustmentApplied={}라 추가 setBudgetData는 no-op)
      setAdjustmentAppliedTeams(new Set());
      setOriginalTargetSales({});
      setApplyPreviousMonthAdjustment(false);
      setIsDataChanged(true);
      // #7: 불러오기가 추가한 로그 직후의 changeLog 길이를 캡처 — 저장/재조회 대기 창(수 초)에
      //   사용자 편집(addChangeLog→로그+dirty)으로 로그가 늘면 저장 성공 후 dirty 해제를 건너뛰기 위한 기준값.
      let changeLogLenAfterImport = 0;
      setChangeLog(prev => {
        const next = [...prev, `${sourceMonth} 월 데이터 불러오기`];
        changeLogLenAfterImport = next.length;
        return next;
      });
      
      logBusinessEvent('월별 데이터 불러오기 완료', { from: sourceMonth, to: currentMonthKey });
      
      // Firebase에 저장 (비동기 처리)
      try {
        // 🔒 '소유 필드 한정' 저장: 복사 필드는 merge/replace로 명시, 그 외(특히 doctorTeamMapping —
        //    대상 월 자체의 일일보고서 의사매핑이므로 불러오기에도 생존해야 함)는 절대 건드리지 않음.
        //    월 통째 교체는 2026-06 의사매핑 소실 사고의 원인 클래스라 금지.
        await saveBudgetMonthReplace(currentMonthKey, {
          employees: ownedMonthFields.employees,
          teams: ownedMonthFields.teams,
          personnelPercentage: ownedMonthFields.personnelPercentage,
          underperformanceDeductionRate: ownedMonthFields.underperformanceDeductionRate,
          applyOvertimeMultiplier: ownedMonthFields.applyOvertimeMultiplier, // ⏰ 150% 토글 — 복사된 초과수당·퇴직금과 쌍으로 영속
          applyPreviousMonthAdjustment: false, // 🔒 #2 조정 토글 off를 DB에도 영속 — 재로드 후 '조정 적용됨' 거짓 표시·자동 재적용 차단
          targetSales: ownedMonthFields.targetSales, // 팀별 합과 일치하는 스칼라 (P1-4)
          importedFromMonth: ownedMonthFields.importedFromMonth,
          lastUpdated: ownedMonthFields.lastUpdated
        }, {
          // 복사/재계산 맵은 통째 교체(대상 월에 옛 팀 유령 키가 남지 않게)
          teamTargetSales: ownedMonthFields.teamTargetSales,
          teamPureTargetSales: ownedMonthFields.teamPureTargetSales,
          teamSalaryTotals: ownedMonthFields.teamSalaryTotals, // 리셋 로스터 기준 재계산값 — 로스터·목표와 일관 쌍
          teamPersonnelPercentages: ownedMonthFields.teamPersonnelPercentages,
          teamIncentiveRatios: ownedMonthFields.teamIncentiveRatios,
          // 🔒 #2 조정 플래그 맵 통째 교체({})로 DB의 잔존 팀 키 전부 삭제 — 미삭제 시 hasAnyFirebaseFlags가
          //    true로 남아 자동적용 이펙트가 재적용을 시도하거나 UI가 '조정 적용됨'을 거짓 표시(순수 목표값과 모순).
          adjustmentApplied: {}, // 팀별 조정 적용 플래그 통째 소거
          // 🔒 이월맵도 통째 소거 — 메모리만 {}로 두고 DB에 옛 이월을 남기면 재조회 때 되살아나
          //    "순수 목표 + 옛 이월"이 다음 저장에서 다시 목표에 더해진다 (Codex 재심 P0-2).
          teamAdjustmentCarryover: {},
          // ⚠️ teamActualSales·teamPerformanceData: 급여는 '재계산 시에만 기록'하는 필드 —
          //    불러오기는 재계산이 아니므로 {} 스냅샷을 DB에 기록하면 안 됨(blind-replace 금지 + 조정 무력화 창).
          //    DB는 비접촉 보존한다 (일일보고서 파이프라인이 재계산·기록).
          teamIncentiveCalculations: {} // 급여 소유 산출 결과 — 불러오기 시 의도적 리셋(재계산됨)
        });

        // 🔧 Firebase 동기화 안전성 강화: 저장 후 대상 월 정합화 (#8 함수형 병합)
        try {
          const reloadedBudgetData = await getBudgetData();
          if (reloadedBudgetData) {
            // 전체 통째 교체(setBudgetData(reloaded)) 대신 prev 기준 대상 월만 덧씌운다 —
            //   재조회 대기 창에 발생한 다른 파이프라인 in-memory 쓰기(actualSales 캐시 등) 롤백 방지.
            //   파이프라인 소유 필드(teamActualSales·teamPerformanceData·totalActualSales)는 in-memory(prev) 최신값 우선.
            setBudgetData(prev => {
              const reloadedMonth = reloadedBudgetData.monthlyData?.[currentMonthKey];
              if (!reloadedMonth) return prev;
              const prevMonth = prev.monthlyData?.[currentMonthKey];
              return {
                ...prev,
                monthlyData: {
                  ...prev.monthlyData,
                  [currentMonthKey]: {
                    ...reloadedMonth,
                    teamActualSales: prevMonth?.teamActualSales ?? reloadedMonth.teamActualSales,
                    teamPerformanceData: prevMonth?.teamPerformanceData ?? reloadedMonth.teamPerformanceData,
                    totalActualSales: prevMonth?.totalActualSales ?? reloadedMonth.totalActualSales,
                    // actualSales 캐시(월별)도 in-memory 최신값 우선 — 주석의 'actualSales 캐시' 보호 약속과 정합
                    actualSales: prevMonth?.actualSales ?? reloadedMonth.actualSales,
                  }
                }
              };
            });
          }
        } catch (reloadError) {
          console.warn('⚠️ [Firebase 동기화] 데이터 재로드 실패, 로컬 상태 유지:', reloadError);
        }

        // 🔧 저장 성공 = 급여 소유 필드 기준 상태==DB → 불러오기가 켠 dirty 해제 (유령 dirty 방지)
        //    미해제 시 무편집 탭에 매월 유령 dirty가 잔존해 beforeunload 경고가 상시 발화하고,
        //    월 이동 프롬프트 '예'가 stale 스냅샷으로 teamPerformanceData를 통째 교체하는 경로를 장전함.
        //    저장 실패 경로(catch)는 dirty 유지 — 사용자가 [저장]으로 마무리해야 함.
        // 🔒 #7: 저장/재조회 대기 창(수 초)에 사용자가 편집(addChangeLog)했으면 dirty 해제를 건너뛴다.
        //    beforeunload가 isDataChanged만 보므로 두 신호를 함께 조건부 해제. flushSync로 현재
        //    changeLog 길이를 동기 확인(불러오기 push 직후 캡처값 대비 순증가 여부).
        let currentChangeLogLen = changeLogLenAfterImport;
        flushSync(() => {
          setChangeLog(prev => { currentChangeLogLen = prev.length; return prev; });
        });
        if (currentChangeLogLen <= changeLogLenAfterImport) {
          setIsDataChanged(false);
          setChangeLog([]);
        } else {
          logDataOperation('불러오기 dirty 해제 스킵 — 저장 대기 창 사용자 편집 감지', {
            capturedLen: changeLogLenAfterImport,
            currentLen: currentChangeLogLen,
          });
        }

        // 🔧 복사된 데이터 정보를 포함한 명확한 성공 메시지
        const copiedDataInfo = [];
        if (selectedMonthData.employees?.length > 0) copiedDataInfo.push(`직원 ${selectedMonthData.employees.length}명`);
        if (selectedMonthData.teams?.length > 0) copiedDataInfo.push(`팀 ${selectedMonthData.teams.length}개`);
        if (Object.keys(recomputedPureTargets).length > 0) copiedDataInfo.push('팀별 목표매출(재산출)');
        copiedDataInfo.push('연장·개별수당은 0으로 초기화');
        if (Object.keys(selectedMonthData.teamPersonnelPercentages || {}).length > 0 || selectedMonthData.personnelPercentage !== undefined) copiedDataInfo.push('인건비율');
        if (selectedMonthData.underperformanceDeductionRate !== undefined) copiedDataInfo.push('차감율');

        const dataInfoText = copiedDataInfo.length > 0 ? ` (${copiedDataInfo.join(', ')})` : '';
        if (!options.silent) {
          alert(`${sourceMonth} 월 데이터를 성공적으로 불러와 저장했습니다.${dataInfoText}\n\n월별 데이터 상태로 변경되었습니다.`);
        }
        return { success: true, dataInfoText };
      } catch (saveError) {
        logError('데이터 저장 실패', saveError);

        // 🔧 구체적인 에러 정보 제공
        const errorMessage = saveError instanceof Error ? saveError.message : String(saveError);
        const isNetworkError = errorMessage.toLowerCase().includes('network') ||
                              errorMessage.toLowerCase().includes('connection') ||
                              errorMessage.toLowerCase().includes('timeout');

        const userMessage = isNetworkError
          ? '네트워크 문제로 저장에 실패했습니다.\n인터넷 연결을 확인한 후 저장 버튼을 눌러 다시 저장해주세요.'
          : `데이터를 불러왔지만 저장하지 못했습니다.\n오류: ${errorMessage}\n\n나중에 저장 버튼을 눌러 저장해주세요.`;

        if (!options.silent) alert(userMessage); // 자동 경로는 래퍼가 단일 폴백 안내 (이중 alert 방지)
      }
    } catch (error) {
      logError('월별 데이터 불러오기 실패', error);

      // 🔧 구체적인 에러 분석 및 해결책 제시
      const errorMessage = error instanceof Error ? error.message : String(error);

      let userMessage = '월별 데이터를 불러오는 데 실패했습니다.\n\n';

      if (errorMessage.includes('monthlyData') || errorMessage.includes('undefined')) {
        userMessage += '선택한 월의 데이터가 존재하지 않거나 손상되었을 수 있습니다.\n다른 월을 선택해보세요.';
      } else if (errorMessage.toLowerCase().includes('network') ||
                errorMessage.toLowerCase().includes('connection')) {
        userMessage += '네트워크 연결 문제가 발생했습니다.\n인터넷 연결을 확인한 후 다시 시도해주세요.';
      } else if (errorMessage.toLowerCase().includes('permission')) {
        userMessage += '데이터 접근 권한이 부족합니다.\n관리자에게 문의해주세요.';
      } else {
        userMessage += `오류 내용: ${errorMessage}\n\n문제가 계속되면 관리자에게 문의해주세요.`;
      }

      if (!options.silent) alert(userMessage); // 자동 경로는 래퍼가 단일 폴백 안내 (이중 alert 방지)
    }
    return { success: false, dataInfoText: '' }; // 저장 실패/예외 경로 (알림은 위에서 처리됨)
  }, [selectedMonth, budgetData, setBudgetData, setEmployees, setImportedFromMonth]);

  const handleImportPreviousMonth = useCallback(async () => {
    logUserAction('월별 데이터 불러오기 시작');
    
    // 사용 가능한 월별 데이터 목록 가져오기
    const availableMonths = budgetData.monthlyData ? Object.keys(budgetData.monthlyData).sort().reverse() : [];
    
    if (availableMonths.length === 0) {
      alert('불러올 수 있는 월별 데이터가 없습니다.\n\n다른 월에서 먼저 데이터를 입력한 후 불러오기 기능을 사용해주세요.');
      return;
    }

    // 지난달 계산
    const [year, month] = selectedMonth.split('-').map(Number);
    let previousMonth = month - 1;
    let previousYear = year;
    
    if (previousMonth === 0) {
      previousMonth = 12;
      previousYear -= 1;
    }
    
    const previousMonthKey = `${previousYear}-${previousMonth.toString().padStart(2, '0')}`;
    const currentMonthKey = selectedMonth;
    
    // 지난달 데이터가 있으면 기본값으로 설정, 없으면 가장 최근 월을 기본값으로 설정
    const defaultMonth = availableMonths.includes(previousMonthKey) ? previousMonthKey : availableMonths[0];
    
    // 사용 가능한 월 목록 표시 및 선택 요청
    const monthListText = availableMonths.join(', ');
    const sourceImportMonth = window.prompt(
      `불러올 월을 선택하세요.\n\n사용 가능한 월: ${monthListText}\n\n기본값: ${defaultMonth}`,
      defaultMonth
    );

    if (!sourceImportMonth) {
      return; // 사용자가 취소한 경우
    }

    // 선택한 월의 데이터가 있는지 확인
    if (!budgetData.monthlyData || !budgetData.monthlyData[sourceImportMonth]) {
      alert(`${sourceImportMonth} 월 데이터가 없습니다.\n\n해당 월에 데이터를 먼저 입력한 후 다시 시도해주세요.`);
      return;
    }
    
    // 🔧 더 명확한 확인 메시지 (어떤 데이터가 복사될지 미리 안내)
    const sourceData = budgetData.monthlyData[sourceImportMonth];
    const dataTypesToCopy = [];
    if (sourceData.employees?.length > 0) dataTypesToCopy.push(`직원 ${sourceData.employees.length}명`);
    if (sourceData.teams?.length > 0) dataTypesToCopy.push(`팀 ${sourceData.teams.length}개`);
    if (Object.keys(sourceData.teamPersonnelPercentages || {}).length > 0 || sourceData.personnelPercentage !== undefined) dataTypesToCopy.push('인건비율');
    if (sourceData.underperformanceDeductionRate !== undefined) dataTypesToCopy.push('차감율');
    dataTypesToCopy.push('팀별 목표매출은 재산출');

    const copyInfo = dataTypesToCopy.length > 0 ? `\n\n복사될 데이터: ${dataTypesToCopy.join(', ')}` : '';

    if (!window.confirm(`${sourceImportMonth} 월의 데이터를 ${currentMonthKey} 월로 가져오시겠습니까?${copyInfo}\n\n※ 연장근로시간·초과수당·개별수당·인센티브는 0으로 초기화됩니다 (그 달 실제 발생값).\n\n기존 ${currentMonthKey} 월 데이터는 덮어쓰기됩니다.`)) {
      return;
    }

    await executeImportFromMonth(sourceImportMonth);
  }, [budgetData, selectedMonth, executeImportFromMonth]);

  // ── 자동 불러오기: 매월 첫 열람 시 실행 (요구 '1일 0시' → 클라이언트 구현은 달 전환 후 첫 열람) ──
  // ⏱️ 한계(의도된 설계): 현재 월 화면을 켜둔 채 자정에 달이 넘어가도 실시간 재평가 계기는 없음 —
  //    selectedMonth가 새 달로 바뀌는 '달 전환 후 첫 (재)열람' 시 정상 발화 (데이터·금액 영향 없는 순수 지연 갭).
  // 조건: 표시 달 == 실제 현재 월 && 월 로드 완료(loadedMonth 신호) && '첫 로드 완료 관찰' 시점에 직원 0명
  //       && 급여 소유 마커(lastUpdated·selectedMonth·dataSource) 부재 — 급여가 저장한 적 있는 달은
  //       '의도적으로 전부 삭제하고 저장한 달'일 수 있어 재진입(재마운트) 시에도 부활시키지 않음.
  //  - ref 선점을 빈 달 판정보다 먼저 해 "직원을 의도적으로 전부 삭제한 달"에 사후 발화하지 않음 (첫 열람 1회 시맨틱)
  //  - 소스 선택·복사 원본 모두 Firestore 신선 스냅샷(fresh) 기준: 직전 달부터 거슬러 직원이 있는 가장
  //    최근 달을 골라 그 월 레코드를 sourceData 옵션으로 코어에 전달 — 장수 탭의 stale state 기준 복사는
  //    다른 세션 최종본 대신 구버전 로스터를 새 달로 silent 저장할 수 있어 금지.
  //  - 다중 탭 레이스: 쓰기 직전 Firestore 신선 재조회로 빈 달 재확인(재조회 실패 시 fail-closed 포기)
  //    + monthChanged 라이브 ref로 월 전환 시 파기
  const autoImportAttemptedRef = useRef<string>('');
  // 월 전환 감지용 라이브 ref (cleanup 기반 취소는 budgetData 정체성 변화 등 무관한 deps 재실행에도
  // 발화해 정당한 자동 실행을 죽이므로, '진짜 월 전환'만 정확히 감지하는 ref 비교를 쓴다)
  const liveSelectedMonthRef = useRef(selectedMonth);
  liveSelectedMonthRef.current = selectedMonth;
  // fetch 창 동안 사용자가 빈 달에 직원을 추가한 경우 감지용 (클로저 employees는 이펙트 실행 시점 스냅샷)
  const liveEmployeesCountRef = useRef(employees.length);
  liveEmployeesCountRef.current = employees.length;
  useEffect(() => {
    const now = new Date();
    const currentYearMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    if (selectedMonth !== currentYearMonth) return; // 현재 월 한정
    if (loadedMonth !== selectedMonth) return;      // 로딩 전 가짜 빈 상태 오발동 차단
    if (autoImportAttemptedRef.current === selectedMonth) return; // 마운트당 월 1회 (첫 로드 완료 관찰 시 선점)
    // 저장/직원추가/로드 진행 중이면 이번 실행은 보류 (ref 미선점 → 다음 재실행에서 재평가)
    if (isSavingRef.current || isAddingEmployeeRef.current || isLoadingDataRef.current) return;
    autoImportAttemptedRef.current = selectedMonth;
    // ref 선점을 빈 달 판정보다 먼저: "직원을 의도적으로 전부 삭제한 달"에 사후 발화하지 않음 (첫 열람 1회 시맨틱)
    if (employees.length > 0) return;               // 첫 관찰에 데이터 있음 → 이 달은 자동 대상 아님(선점 유지)
    // 🔒 급여 소유 마커 존재 = 급여가 이 달을 저장한 적 있음 → 직원 0명이어도 '의도적으로 비운 달'일 수
    //    있어 스킵. ref 선점은 마운트 수명이라 페이지 이탈·재진입 시 초기화되지만 마커는 DB에 남으므로
    //    전부 삭제·저장한 달이 재진입 시 지난달 데이터로 무확인 부활하지 않음.
    //    (실적 파이프라인은 이 마커들을 쓰지 않아 일일보고서만 먼저 올라온 새 달은 정상 발화)
    const hasPayrollMarker = (rec: any): boolean =>
      !!rec && (rec.lastUpdated !== undefined || rec.selectedMonth !== undefined || rec.dataSource !== undefined);
    const stateMonthRec = budgetData.monthlyData?.[selectedMonth];
    if ((stateMonthRec?.employees?.length || 0) > 0) return;
    if (hasPayrollMarker(stateMonthRec)) return;

    const targetMonth = selectedMonth;
    const monthChanged = () => liveSelectedMonthRef.current !== targetMonth;
    (async () => {
      try {
        // 다중 탭/사용자 레이스 가드: 쓰기 직전 신선 재조회로 빈 달 재확인
        const fresh = await getBudgetData();
        if (monthChanged()) return; // 대기 중 월 전환 — 전환된 월 화면에 소스 로스터 적용 방지
        if (!fresh) {
          // 🔒 fail-closed: 재조회 실패(null)면 빈 달 재확인·소스 검증이 모두 불가능 — 자동 실행 포기.
          //    (fail-open이면 가드가 지키려던 시나리오 — 다른 세션 선점·의도적 빈 달 — 가 조용히 열림)
          //    ref 선점은 유지(마운트당 1회 시맨틱): 알림 없이 조용히 스킵하고, 재마운트 시 ref가
          //    초기화되어 자연 재시도됨. 수동 [지난달 내역 불러오기]는 영향 없음.
          logDataOperation('자동 불러오기 스킵 — 신선 재조회 실패(fail-closed)', { month: targetMonth });
          return;
        }
        const freshMonthRec = fresh.monthlyData?.[targetMonth];
        if ((freshMonthRec?.employees?.length || 0) > 0) {
          logDataOperation('자동 불러오기 스킵 — 다른 세션이 먼저 채움', { month: targetMonth });
          return;
        }
        // 급여 소유 마커는 fresh 기준으로도 재확인 (다른 세션이 '전부 삭제 후 저장'한 직후의 재진입 케이스)
        if (hasPayrollMarker(freshMonthRec)) {
          logDataOperation('자동 불러오기 스킵 — 급여 저장 이력 있는 달(의도적 빈 달 가능)', { month: targetMonth });
          return;
        }
        const freshMonthly = fresh.monthlyData || {};
        // 소스 선택도 fresh 기준으로 통일 — 복사 원본이 fresh 스냅샷(sourceData)이므로
        // stale state 단독 존재 월·구버전 로스터 월 채택을 원천 차단
        const sourceMonth = Object.keys(freshMonthly)
          .filter(m => m < targetMonth && ((freshMonthly[m]?.employees?.length || 0) > 0))
          .sort()
          .pop();
        if (!sourceMonth) return; // 불러올 원본 자체가 없음(초기 상태) — 조용히 스킵
        if (monthChanged()) return;
        // fetch 창 동안의 사용자 개입 재확인: 직원을 추가했거나 저장/로드가 시작됐으면 자동 실행 포기
        if (liveEmployeesCountRef.current > 0 || isSavingRef.current || isLoadingDataRef.current) return;
        logUserAction('지난달 내역 자동 불러오기 실행', { sourceMonth, targetMonth });
        const { success, dataInfoText } = await executeImportFromMonth(sourceMonth, { silent: true, sourceData: freshMonthly[sourceMonth] });
        if (success) {
          alert(`${sourceMonth} 월 내역을 ${targetMonth} 월로 자동으로 불러왔습니다.${dataInfoText}\n\n(매월 첫 열람 시 자동 실행 — 이미 입력된 달에는 실행되지 않습니다)`);
        } else if (!monthChanged()) {
          alert('지난달 내역 자동 불러오기가 저장까지 완료되지 못했습니다.\n화면에 불러온 내용이 보이면 [저장]을, 아니면 [지난달 내역 불러오기]로 다시 실행해 주세요.');
        }
      } catch (e) {
        logError('자동 불러오기 실패', e);
        if (!monthChanged()) {
          alert('지난달 내역 자동 불러오기에 실패했습니다.\n[지난달 내역 불러오기] 버튼으로 수동 실행해 주세요.');
        }
      }
    })();
  }, [selectedMonth, loadedMonth, employees.length, budgetData, executeImportFromMonth]);


  // ═══════════════════════════════════════
  // Previous Month Data & Adjustment Logic
  // ═══════════════════════════════════════

  // 지난달 데이터 로드 함수
  const loadPreviousMonthData = async () => {
    setIsLoadingPreviousMonthData(true);
    try {
      // 지난달 계산
      const currentDate = new Date(selectedMonth + '-01');
      const previousDate = new Date(currentDate.setMonth(currentDate.getMonth() - 1));
      const previousMonth = `${previousDate.getFullYear()}-${String(previousDate.getMonth() + 1).padStart(2, '0')}`;
      
      // 지난달 월별 데이터 확인
      const prevMonthData = budgetData.monthlyData?.[previousMonth];
      if (prevMonthData) {
        
        setPreviousMonthData({
          month: previousMonth,
          employees: prevMonthData.employees || [],
          teamTargetSales: prevMonthData.teamTargetSales || {},
          teamPerformanceData: prevMonthData.teamPerformanceData || {},
          actualSales: prevMonthData.totalActualSales || 0
        });
        
        // 각 팀별로 데이터 검증
        const teams = Object.keys(prevMonthData.teamTargetSales || {});
        teams.forEach(teamName => {
          const targetSales = prevMonthData.teamTargetSales?.[teamName] || 0;
          const performanceData = prevMonthData.teamPerformanceData?.[teamName];
          const actualSales = performanceData?.actualSales || 0;
          const underperformance = targetSales - actualSales;
          
        });
      } else {
        setPreviousMonthData(null);
      }
    } catch (error) {
      console.error('❌ [지난달 데이터 로드] 오류:', error);
      setPreviousMonthData(null);
    } finally {
      setIsLoadingPreviousMonthData(false);
    }
  };

  // 🔧 지난달 데이터 로드 완료 후 자동 적용 useEffect
  useEffect(() => {
    // 스위치가 활성화되어 있고, 지난달 데이터가 로드되었으며, 현재 로딩 중이 아닐 때 자동 적용
    // 단, 이미 적용된 상태가 아닐 때만 적용
    if (applyPreviousMonthAdjustment && previousMonthData && !isLoadingPreviousMonthData && adjustmentAppliedTeams.size === 0) {
      // Firebase에 저장된 상태인지 확인
      const monthlyData = budgetData.monthlyData?.[selectedMonth];
      const hasSavedAdjustment = monthlyData?.applyPreviousMonthAdjustment === true;
      const hasTeamTargetSales = monthlyData?.teamTargetSales && Object.keys(monthlyData.teamTargetSales).length > 0;
      const hasTeamPureTargetSales = monthlyData?.teamPureTargetSales && Object.keys(monthlyData.teamPureTargetSales).length > 0;
      
      // 🔧 개선된 재적용 방지 로직
      const currentTime = Date.now();
      const isRecentManualToggle = manualToggleTimestamp > 0 && (currentTime - manualToggleTimestamp) < 5000; // 5초 이내

      // 🛡️ 이중 안전장치 시스템 통합: Firebase와 세션 플래그 모두 확인
      if (!isRecentManualToggle && hasSavedAdjustment && hasTeamTargetSales && hasTeamPureTargetSales) {
        // 🔧 중앙집중식 안전장치 플래그 확인 (기존 계산 로직 대신 사용)
        const firebaseAdjustmentFlags = monthlyData?.adjustmentApplied || {};
        const hasAnyFirebaseFlags = Object.keys(firebaseAdjustmentFlags).some(teamName => firebaseAdjustmentFlags[teamName]);

        // 🛡️ 이중 검증: Firebase 플래그와 세션 플래그 모두 확인
        if (hasAnyFirebaseFlags || adjustmentAppliedTeams.size > 0) {

          // 🔄 플래그 동기화 (Firebase → 세션)
          setAdjustmentAppliedTeams(prev => {
            const newSet = new Set(prev);
            Object.keys(firebaseAdjustmentFlags).forEach(teamName => {
              if (firebaseAdjustmentFlags[teamName]) {
                newSet.add(teamName);
              }
            });
            return newSet;
          });

          return;
        } else {
        }
      }

      if (isRecentManualToggle) {
      }
      
      applyAdjustmentToAllEligibleTeams();
    }
  }, [applyPreviousMonthAdjustment, previousMonthData, isLoadingPreviousMonthData, selectedMonth, budgetData.monthlyData, manualToggleTimestamp]);

  // 🛡️ 이중 안전장치 시스템 초기화 및 동기화 useEffect
  useEffect(() => {
    const monthlyData = budgetData.monthlyData?.[selectedMonth];
    if (!monthlyData?.adjustmentApplied) return;

    const firebaseFlags = monthlyData.adjustmentApplied;
    const appliedTeams = Object.keys(firebaseFlags).filter(teamName => firebaseFlags[teamName]);

    if (appliedTeams.length > 0) {

      // Firebase 플래그를 세션 플래그로 동기화
      setAdjustmentAppliedTeams(prev => {
        const newSet = new Set(prev);
        appliedTeams.forEach(teamName => newSet.add(teamName));
        return newSet;
      });

    }
  }, [selectedMonth, budgetData.monthlyData]);

  // 🛠️ 이중 안전장치 복구 및 일관성 검증 useEffect
  useEffect(() => {
    if (!applyPreviousMonthAdjustment || !previousMonthData) return;

    const monthlyData = budgetData.monthlyData?.[selectedMonth];
    const firebaseFlags = monthlyData?.adjustmentApplied || {};

    // 🔍 불일치 감지
    const firebaseAppliedTeams = new Set(Object.keys(firebaseFlags).filter(team => firebaseFlags[team]));
    const sessionAppliedTeams = adjustmentAppliedTeams;

    const firebaseOnly = [...firebaseAppliedTeams].filter(team => !sessionAppliedTeams.has(team));
    const sessionOnly = [...sessionAppliedTeams].filter(team => !firebaseAppliedTeams.has(team));

    // 🛠️ 불일치 복구
    if (firebaseOnly.length > 0 || sessionOnly.length > 0) {

      // 합집합으로 동기화 (더 안전한 방향으로)
      const unifiedTeams = new Set([...firebaseAppliedTeams, ...sessionAppliedTeams]);

      setAdjustmentAppliedTeams(unifiedTeams);

      // (이전에는 세션 전용 팀의 Firebase 플래그를 타이머로 true로 썼다. 이월금액 없이 플래그만
      //  true가 되는 경로였고, 운영 DB의 FLAG_ONLY 불일치 2건이 여기서 나왔다. 플래그는 이제
      //  이월금액을 기록하는 곳에서 함께 맞추므로 여기서는 쓰지 않는다.)

    }
  }, [applyPreviousMonthAdjustment, adjustmentAppliedTeams, selectedMonth, budgetData.monthlyData]);


  // 🔧 토글 off 상태의 세션 정리 — 저장 데이터는 건드리지 않는다.
  //
  //   ⚠️ 이 함수는 토글 off 이펙트에서 '마운트·월 전환마다' 호출된다. 여기서 이월금액을
  //   0으로 쓰면, 이월이 적용된 달(예: 2025-06, 38,234,505원)을 열기만 해도 이월이 지워지고
  //   그 상태로 저장하면 영구 유실된다. 그래서 여기서는 세션 플래그만 비운다.
  //   이월을 실제로 제거하는 것은 원장의 명시적 행동(handleAdjustmentToggle(false), 확인 대화상자)뿐이다.
  const restoreOriginalTargetSales = () => {
    setAdjustmentAppliedTeams(new Set());
    setOriginalTargetSales({});
  };

  // 🔧 모든 조건 맞는 팀에 자동 적용 함수 (중앙집중식 통합 버전)
  const applyAdjustmentToAllEligibleTeams = async () => {
    // 🛡️ 동시 실행 방지
    if (isBulkAdjustmentRunning) {
      return;
    }

    if (!previousMonthData) {
      return;
    }

    setIsBulkAdjustmentRunning(true);

    try {
      const eligibleTeams: Array<{teamName: string, pureTarget: number, finalResult: any}> = [];

      // 🔧 중앙집중식 함수를 사용하여 조건 맞는 팀들 식별
      localTeams.forEach(teamName => {
        // 🚫 "미지정" 팀은 조정 대상에서 제외
        if (teamName === '미지정') {
          if (IS_DEVELOPMENT) {
          }
          return;
        }

        const teamEmployees = employees.filter(emp => emp.team === teamName);
        const monthlyTeamPureTargetSales = budgetData.monthlyData?.[selectedMonth]?.teamPureTargetSales || {};
        const pureTarget = monthlyTeamPureTargetSales[teamName] || 0;

        if (pureTarget > 0) {
          // 🎯 중앙집중식 함수 사용으로 일관된 계산 및 안전장치 적용
          const finalResult = calculateFinalTargetSales(
            teamName,
            pureTarget,
            teamEmployees,
            'bulk' // 일괄 적용 컨텍스트
          );

          // 실제로 조정이 적용되는 팀만 수집
          if (finalResult.adjustmentApplied) {
            eligibleTeams.push({
              teamName,
              pureTarget,
              finalResult
            });
          }
        }
      });

    if (eligibleTeams.length === 0) {
      // 🔇 조정 대상이 없을 때 조용히 종료 (메시지 없음)
      return;
    }

    // 모든 대상 팀에 적용 (순수 목표매출 기준)
    const appliedTeamNames = new Set<string>();
    
    // 팀 성과 데이터 재계산을 위한 배치 업데이트
    setBudgetData(prev => {
      const currentMonthData = getSafeMonthlyData(prev, selectedMonth);
      const newTeamTargetSales = { ...(currentMonthData.teamTargetSales || {}) };
      const newTeamPerformanceData = { ...(currentMonthData.teamPerformanceData || {}) };
      // 🔒 이월금액은 목표와 '같은 업데이트'에 기록한다 — 불변식(목표 = 순수 + 이월)이 한 번에 성립
      const newCarryover: Record<string, number> = { ...(currentMonthData.teamAdjustmentCarryover || {}) };
      
      eligibleTeams.forEach(({teamName, pureTarget, finalResult}) => {

        // 🔧 중앙집중식 함수 결과 사용
        newTeamTargetSales[teamName] = finalResult.finalTargetSales;
        newCarryover[teamName] = finalResult.underperformanceAmount; // 이번에 동결한 이월금액

        // 팀 성과 데이터 재계산 (중앙집중식 결과 반영)
        if (newTeamPerformanceData[teamName]) {
          const actualSales = newTeamPerformanceData[teamName].actualSales || 0;
          newTeamPerformanceData[teamName] = {
            ...newTeamPerformanceData[teamName],
            targetSales: finalResult.finalTargetSales,
            exceededSales: Math.max(0, actualSales - finalResult.finalTargetSales),
            underperformanceSales: Math.max(0, finalResult.finalTargetSales - actualSales),
            achievementRate: finalResult.finalTargetSales > 0 ? actualSales / finalResult.finalTargetSales : 0
          };
        }
  
        appliedTeamNames.add(teamName);
      });

      // 🛡️ Firebase 안전장치 플래그 통합 업데이트
      const currentAdjustmentApplied = currentMonthData.adjustmentApplied || {};
      const updatedAdjustmentApplied = { ...currentAdjustmentApplied };

      // 적용된 팀들에 대해 Firebase 플래그 설정
      eligibleTeams.forEach(({teamName}) => {
        updatedAdjustmentApplied[teamName] = true;
      });

      const newTotalTargetSales = Object.values(newTeamTargetSales).reduce((sum: number, v: any) => sum + (Number(v) || 0), 0);
      const updatedMonthlyData = {
        ...prev.monthlyData,
        [selectedMonth]: {
          ...currentMonthData,
          teamTargetSales: newTeamTargetSales,
          teamAdjustmentCarryover: newCarryover, // 🔒 목표와 함께
          targetSales: newTotalTargetSales,
          teamPerformanceData: newTeamPerformanceData,
          adjustmentApplied: updatedAdjustmentApplied, // 정보용 플래그 (이월>0 과 동치)
          lastUpdated: new Date().toISOString()
        }
      };

      return {
        ...prev,
        teamTargetSales: newTeamTargetSales,
        monthlyData: updatedMonthlyData
      };
    });

    // 🛡️ 세션 플래그 업데이트 (중앙집중식과 동일)
    setAdjustmentAppliedTeams(prev => {
      const newSet = new Set(prev);
      appliedTeamNames.forEach(teamName => newSet.add(teamName));
      return newSet;
    });

    } catch (error) {
      console.error('❌ [통합 일괄 조정] 실행 중 오류:', error);
      alert('일괄 조정 중 오류가 발생했습니다. 다시 시도해주세요.');
    } finally {
      setIsBulkAdjustmentRunning(false);
    }
  };

  // 🔧 지난달 미달성 조정 스위치 핸들러 (확인 다이얼로그 포함)
  const handleAdjustmentToggle = (newState: boolean) => {

    if (newState) {
      // 활성화 시 확인 다이얼로그
      const confirmMessage = `지난달 미달성 금액 상향 조정을 활성화하시겠습니까?\n\n` +
        `조건에 맞는 모든 팀의 목표매출이 자동으로 조정됩니다.\n` +
        `(팀명과 팀장이 지난달과 동일하고 미달성이 있는 팀)`;
        
      if (window.confirm(confirmMessage)) {
        // 🔧 수동 토글 타임스탬프 기록
        setManualToggleTimestamp(Date.now());
        setApplyPreviousMonthAdjustment(true);
        
        // 월별 데이터에 활성화 상태 저장
        setBudgetData(prev => {
          const currentMonthData = getSafeMonthlyData(prev, selectedMonth);
          const updatedMonthlyData = {
            ...prev.monthlyData,
            [selectedMonth]: {
              ...currentMonthData,
              targetSales: currentMonthData.targetSales,
              actualSales: currentMonthData.actualSales,
              applyPreviousMonthAdjustment: true, // 활성화 상태 저장
              lastUpdated: new Date().toISOString()
            }
          };
          
          const updatedBudgetData: BudgetData = {
            ...prev,
            monthlyData: updatedMonthlyData
          };
          
          // 자동 저장 제거 - 수동 저장으로 변경
          // 변경사항만 추적하고 실제 저장은 저장 버튼 클릭 시에만 수행
          setIsDataChanged(true);
          
          return updatedBudgetData;
        });
        
        // 지난달 데이터가 이미 로드되어 있으면 즉시 적용
        if (previousMonthData) {
          applyAdjustmentToAllEligibleTeams();
        }
        // 그렇지 않으면 loadPreviousMonthData가 완료된 후 자동 적용을 위해 별도 useEffect에서 처리
      }
    } else {
      // 비활성화 시 확인 다이얼로그
      const confirmMessage = `지난달 미달성 금액 상향 조정을 비활성화하시겠습니까?\n\n` +
        `모든 팀의 최종 목표매출이 순수 목표매출과 동일하게 설정됩니다.`;
        
      if (window.confirm(confirmMessage)) {
        setApplyPreviousMonthAdjustment(false);
        
        // 비활성화 시 모든 팀의 최종 목표매출을 순수 목표매출로 재설정
        setBudgetData(prev => {
          const currentMonthData = getSafeMonthlyData(prev, selectedMonth);
          const teamPureTargetSales = currentMonthData.teamPureTargetSales || {};
          const newTeamTargetSales = { ...teamPureTargetSales }; // 순수 목표매출로 복사
          
          // 팀 성과 데이터 재계산 (순수 목표매출 기준)
          const newTeamPerformanceData: Record<string, any> = {};
          if (currentMonthData.teamPerformanceData) {
            Object.entries(currentMonthData.teamPerformanceData).forEach(([teamName, perfData]: [string, any]) => {
              const pureTarget = teamPureTargetSales[teamName] || 0;
              const actualSales = perfData.actualSales || 0;
              
              // 순수 목표매출 기준으로 초과/미달 재계산
              newTeamPerformanceData[teamName] = {
                ...perfData,
                targetSales: pureTarget,
                exceededSales: Math.max(0, actualSales - pureTarget),
                underperformanceSales: Math.max(0, pureTarget - actualSales),
                achievementRate: pureTarget > 0 ? actualSales / pureTarget : 0
              };
            });
          }
          
          // 해제 = 이월금액 0 + 플래그 false + 목표를 순수목표로 — 한 업데이트에 함께.
          // 이전에는 목표만 순수로 내리고 플래그·이월은 두어 "플래그 true + 이월 없는 목표"가 남았다.
          const clearedCarryover: Record<string, number> = {};
          const clearedFlags: Record<string, boolean> = {};
          new Set([
            ...Object.keys(currentMonthData.teamAdjustmentCarryover || {}),
            ...Object.keys(currentMonthData.adjustmentApplied || {}),
            ...Object.keys(newTeamTargetSales),
          ]).forEach(teamName => { clearedCarryover[teamName] = 0; clearedFlags[teamName] = false; });
          const loweredTotal = Object.values(newTeamTargetSales).reduce((sum: number, v: any) => sum + (Number(v) || 0), 0);

          const updatedMonthlyData = {
            ...prev.monthlyData,
            [selectedMonth]: {
              ...currentMonthData,
              targetSales: loweredTotal,
              actualSales: currentMonthData.actualSales || 0,
              teamTargetSales: newTeamTargetSales,
              teamAdjustmentCarryover: clearedCarryover,
              adjustmentApplied: clearedFlags,
              teamPerformanceData: newTeamPerformanceData,
              applyPreviousMonthAdjustment: false, // 비활성화 상태 저장
              lastUpdated: new Date().toISOString()
            }
          };
          
          // Firebase에 저장
          const updatedBudgetData: BudgetData = {
            ...prev,
            teamTargetSales: newTeamTargetSales,
            monthlyData: updatedMonthlyData
          };
          
          // 자동 저장 제거 - 수동 저장으로 변경
          // 변경사항만 추적하고 실제 저장은 저장 버튼 클릭 시에만 수행
          setIsDataChanged(true);
          
          return updatedBudgetData;
        });
        
        // 조정 적용 상태 초기화
        setAdjustmentAppliedTeams(new Set());
        setOriginalTargetSales({});
      }
    }
  };

  return {
    lastLoadedMonthRef,
    isLoadingDataRef,
    loadedMonth,
    handleImportPreviousMonth,
    loadPreviousMonthData,
    restoreOriginalTargetSales,
    applyAdjustmentToAllEligibleTeams,
    handleAdjustmentToggle,
  };
}
